import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { geoAlbersUsa, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import topo from "./usStates.topojson.json";
import CITY_COORDS from "./cityCoords";
import { fig } from "../../theme";

// Projection + state outlines are computed once at module load.
const W = 900;
const H = 560;
const STATES_FC = feature(topo, topo.objects.states);
const PROJECTION = geoAlbersUsa().fitSize([W, H], STATES_FC);
const PATH_GEN = geoPath(PROJECTION);
const STATE_PATHS = STATES_FC.features.map((f) => PATH_GEN(f));

/**
 * CityBubbleMap — a geographic heat map of U.S. visitors as circular, radial-
 * gradient bubbles positioned by city and sized by view count. City names are
 * resolved to coordinates via the bundled top-1000 U.S. city table.
 */
export default function CityBubbleMap({ events, maxHeight = 480 }) {
  const bubbles = useMemo(() => {
    const groups = new Map();
    (events || []).forEach((ev) => {
      if (!ev || !ev.City) return;
      const city = String(ev.City).trim();
      const state = String(ev.State || "").trim();
      if (!city) return;
      const key = `${city}|${state}`.toLowerCase();
      const coords = CITY_COORDS[key];
      if (!coords) return; // unknown / non-mappable city
      const g = groups.get(key) || { city, state, lat: coords[0], lon: coords[1], count: 0 };
      g.count += 1;
      groups.set(key, g);
    });
    return Array.from(groups.values())
      .map((g) => {
        const p = PROJECTION([g.lon, g.lat]);
        return p ? { ...g, x: p[0], y: p[1] } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.count - a.count); // largest first (drawn under smaller)
  }, [events]);

  const max = bubbles.reduce((m, b) => Math.max(m, b.count), 0) || 1;
  const radiusFor = (c) => 6 + 26 * Math.sqrt(c / max);

  if (bubbles.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center", color: "text.secondary", fontSize: "0.9rem" }}>
        No mappable city data yet.
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ width: "100%", maxWidth: 820, mx: "auto" }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          style={{ display: "block", height: "auto", maxHeight }}
          role="img"
          aria-label="United States visitors by city"
        >
          <defs>
            <radialGradient id="sk12-bubble" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={fig.teal} stopOpacity="0.85" />
              <stop offset="55%" stopColor={fig.teal} stopOpacity="0.45" />
              <stop offset="100%" stopColor={fig.green} stopOpacity="0.04" />
            </radialGradient>
          </defs>

          {/* State outlines */}
          {STATE_PATHS.map((d, i) => (
            <path key={i} d={d || ""} fill="#eef2f4" stroke="#ffffff" strokeWidth={1} />
          ))}

          {/* City bubbles */}
          {bubbles.map((b) => (
            <circle
              key={`${b.city}|${b.state}`}
              cx={b.x}
              cy={b.y}
              r={radiusFor(b.count)}
              fill="url(#sk12-bubble)"
              stroke={fig.tealDark}
              strokeWidth="1"
              strokeOpacity="0.5"
            >
              <title>{`${b.city}, ${b.state}: ${b.count.toLocaleString()} views`}</title>
            </circle>
          ))}
        </svg>
      </Box>

      {/* Top-cities caption */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "center", mt: 1 }}>
        {bubbles.slice(0, 6).map((b) => (
          <Typography key={`${b.city}|${b.state}`} variant="caption" sx={{ color: "text.secondary" }}>
            <Box
              component="span"
              sx={{
                display: "inline-block",
                width: 9,
                height: 9,
                borderRadius: "50%",
                background: fig.teal,
                mr: 0.6,
                verticalAlign: "middle",
              }}
            />
            {b.city} ({b.count.toLocaleString()})
          </Typography>
        ))}
      </Box>
    </Box>
  );
}
